import {
  eventDispatcher,
  eventListener,
  ISubscribeTheme,
  TAL_THEME_CHANGE,
  TOPICS,
} from "@origin-digital/event-dispatcher";
import { OriginThemeMode } from "@origin-digital/ods-types";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { isOnServer } from "@origin-digital/tal-common";
import { THEME } from "@origin-digital/tal-client-common";

interface ThemeChangeEvent extends MessageEvent {
  data: OriginThemeMode;
}

// Extend the Document interface to include the custom event
declare global {
  interface DocumentEventMap {
    [TAL_THEME_CHANGE]: ThemeChangeEvent;
  }
}

export const isValidTheme = (theme: string): theme is OriginThemeMode => {
  return theme === THEME.LIGHT || theme === THEME.DARK;
};

export const DEFAULT_THEME_MODE: OriginThemeMode = THEME.LIGHT;

const useThemeEffect =
  typeof window === "undefined" || isOnServer() ? useEffect : useLayoutEffect;

/**
 * Subscribe and unsubscribe to SUBSCRIBE_THEME events, and handle fetching initial theme.
 */
export const useThemeListener = () => {
  const subscribersList = useRef<
    ((theme: OriginThemeMode, isPageload: boolean) => void)[]
  >([]);
  const confirmedThemeRef = useRef<OriginThemeMode>();
  const [currentThemeMode, setCurrentThemeMode] =
    useState<OriginThemeMode>(DEFAULT_THEME_MODE);

  const publishToSubscribers = (theme: OriginThemeMode, isPageload = false) => {
    confirmedThemeRef.current = theme;
    setCurrentThemeMode(theme);
    subscribersList.current.forEach((subscriber) => {
      subscriber(theme, isPageload);
    });
  };

  useThemeEffect(() => {
    let active = true;
    let receivedThemeChange = false;
    // ============================================================================
    // Create subscribers list
    // ============================================================================
    const handleSubscribeTheme = (event: ISubscribeTheme) => {
      const { subscriber } = event.payload;
      subscribersList.current.push(subscriber);
      if (confirmedThemeRef.current !== undefined) {
        subscriber(confirmedThemeRef.current, true);
      }
      const unsubscribe = () => {
        const index = subscribersList.current.indexOf(subscriber);
        if (index > -1) {
          subscribersList.current.splice(index, 1);
        }
      };

      return unsubscribe;
    };
    const unsubscribeTheme = eventListener.addListener(
      TOPICS.SUBSCRIBE_THEME,
      handleSubscribeTheme
    );

    // ============================================================================
    // Add listener to notify subscribers on theme change
    // ============================================================================
    const handleThemeChange = (event: ThemeChangeEvent) => {
      const newTheme = event?.data;
      if (isValidTheme(newTheme)) {
        receivedThemeChange = true;
        publishToSubscribers(newTheme);
      }
    };
    document.addEventListener(TAL_THEME_CHANGE, handleThemeChange);

    // ============================================================================
    // Fetch initial theme and notify subscribers
    // ============================================================================
    const fetchTheme = async () => {
      // fetch current theme via mesh
      const result = await eventDispatcher.dispatch({
        topic: TOPICS.FETCH_THEME,
        payload: {},
      });

      const initialTheme = result?.theme;
      if (active && !receivedThemeChange && isValidTheme(initialTheme)) {
        publishToSubscribers(initialTheme, true);
      }
    };
    fetchTheme();

    return () => {
      active = false;
      unsubscribeTheme?.();
      document.removeEventListener(TAL_THEME_CHANGE, handleThemeChange);
    };
  }, []);

  return currentThemeMode;
};
